import React, { useEffect, useState } from 'react';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../TwAutocomplete/TwAutocomplete';
import { useGetDriversQuery } from '../../redux/api/driver/driverApi';
import { Equipment } from '../../redux/api/equipment/equipment.model';
import StringUtils from '../../utils/stringUtils';
import CreateDriver from '../../pages/Drivers/CreateDriver/CreateDriver';
import { CarrierResponse } from '../../redux/api/carriers/carriers.model';
import { Driver } from '../../redux/api/driver/driver.model';
import { SxProps } from '@mui/material';
import useAuth from '../../providers/Auth/Auth.provider';
import { USER_ROLE } from '../../providers/User/User.model';

type DriverSelectorProps = {
  label?: string;
  selectedDriver: TwAutocompleteOption | null | undefined;
  hasError: boolean;
  onChange: (value: string) => void;
  setSelectedDriver: (driver: TwAutocompleteOption | null) => void;
  setOpenDriverModal?: (open: boolean) => void;
  fromDocManager?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  sx?: SxProps;
  noActions?: boolean;
};

const TwDriverSelector: React.FC<DriverSelectorProps> = ({
  label,
  selectedDriver,
  hasError,
  setSelectedDriver,
  setOpenDriverModal,
  onChange,
  fromDocManager = false,
  disabled = false,
  readOnly = false,
  sx = {},
  noActions = false
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const [driverOptions, setDriverOptions] = React.useState<TwAutocompleteOption[]>([]);
  const [searchDriver, setSearchDriver] = useState<string>('');
  const [blockFetchDrivers, setBlockFetchDrivers] = useState<boolean>(false);
  const [openCreateDriverModal, setOpenCreateDriverModal] = useState<boolean>(false);

  // Fetch driver list
  const { data: driverList = { content: [], total: 0 } } = useGetDriversQuery(
    {
      search: searchDriver,
      offset: 0,
      limit: 10
    },
    {
      skip: blockFetchDrivers || readOnly
    }
  );

  const getDriverName = (driver: Driver): string => {
    return `${StringUtils.capitalizeFirstLetter(
      driver.first_name.toLowerCase()
    )} ${StringUtils.capitalizeFirstLetter(driver.last_name.toLowerCase())}`;
  };

  // Transform driver list
  const transformDriverList = (drivers: Driver[]): TwAutocompleteOption[] => {
    return drivers.map((driver) => ({
      id: driver.id,
      value: driver.id,
      label: getDriverName(driver)
    }));
  };

  // Open create driver modal
  const handleOpenCreateDriver = (): void => {
    setOpenDriverModal && setOpenDriverModal(true);
    setOpenCreateDriverModal(true);
  };

  // Close create driver modal
  const handleCloseCreateDriver = (): void => {
    setOpenDriverModal && setOpenDriverModal(false);
    setOpenCreateDriverModal(false);
  };

  // Create driver success
  const handleCreateDriverSuccess = (
    newDriver: Driver,
    selectedTruck?: Equipment | undefined,
    selectedCarrier?: CarrierResponse | undefined,
    selectedTrailer?: Equipment | undefined
  ): void => {
    setSelectedDriver({
      id: newDriver.id,
      value: newDriver.id,
      label: getDriverName(newDriver),
      subLabel: newDriver.email_address
    });
    onChange(newDriver.id);
  };

  useEffect(() => {
    if (driverList.content.length > 0) {
      setBlockFetchDrivers(true);
      setDriverOptions(transformDriverList(driverList.content));
    }
  }, [JSON.stringify(driverList.content)]);

  return (
    <>
      <TwAutocomplete
        label={label}
        name="driver"
        filterOptions={(x: any): any => x}
        renderOption={(props, option): React.ReactNode => {
          return <RenderOption key={option.id} props={props} option={option} />;
        }}
        optionList={driverOptions}
        value={selectedDriver || null}
        onChange={(_, newValue: TwAutocompleteOption | null): void => {
          const newDriver = newValue ? ({ ...newValue } as TwAutocompleteOption) : null;
          setSelectedDriver(newDriver);
          onChange(newValue?.id || '');
        }}
        onInputChange={(_, newInputValue): void => {
          setSearchDriver(newInputValue || '');
          setBlockFetchDrivers(false);
        }}
        listAction={
          (userData?.role !== USER_ROLE.DRIVER && !readOnly) || !noActions
            ? {
                label: 'New driver',
                action: handleOpenCreateDriver
              }
            : undefined
        }
        error={hasError}
        readonly={readOnly}
        showEndAdornment={!readOnly}
        disabled={disabled}
        sx={sx}
      />

      {openCreateDriverModal && (
        <CreateDriver
          open={openCreateDriverModal}
          onClose={handleCloseCreateDriver}
          onSuccess={handleCreateDriverSuccess}
          fromDocManager={fromDocManager}
        />
      )}
    </>
  );
};

export default TwDriverSelector;
